import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Loader2, Sparkles, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { reviseAgentProposal } from "../../services/crmServices/aiAgentService";

// ── Simple line diff (LCS) ─────────────────────────────────────────────────
export function diffLines(oldText, newText) {
    const a = (oldText || "").split("\n");
    const b = (newText || "").split("\n");
    const m = a.length, n = b.length;
    const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = m - 1; i >= 0; i--) {
        for (let j = n - 1; j >= 0; j--) {
            dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
        }
    }
    const out = [];
    let i = 0, j = 0;
    while (i < m && j < n) {
        if (a[i] === b[j]) { out.push({ type: "same", text: a[i] }); i++; j++; }
        else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ type: "del", text: a[i] }); i++; }
        else { out.push({ type: "add", text: b[j] }); j++; }
    }
    while (i < m) { out.push({ type: "del", text: a[i] }); i++; }
    while (j < n) { out.push({ type: "add", text: b[j] }); j++; }
    return out;
}

export function DiffView({ oldText, newText }) {
    const lines = diffLines(oldText, newText);
    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[50vh] overflow-y-auto bg-white">
            <pre className="text-[11px] leading-relaxed whitespace-pre-wrap break-words m-0">
                {lines.map((l, i) => (
                    <div
                        key={i}
                        className={
                            l.type === "add" ? "bg-emerald-50 text-emerald-800 px-3 py-0.5" :
                            l.type === "del" ? "bg-red-50 text-red-700 px-3 py-0.5 line-through opacity-70" :
                            "px-3 py-0.5 text-gray-600"
                        }
                    >
                        {l.type === "add" ? "+ " : l.type === "del" ? "− " : "  "}{l.text || " "}
                    </div>
                ))}
            </pre>
        </div>
    );
}

const TOOL_ACTION_STYLES = {
    create: "bg-emerald-100 text-emerald-700",
    update: "bg-amber-100 text-amber-700",
    delete: "bg-red-100 text-red-700"
};

export function ToolChangeCard({ tc }) {
    return (
        <div className="border border-gray-200 rounded-lg p-2.5 bg-white text-[11px]">
            <div className="flex items-center gap-1.5 flex-wrap">
                <Badge className={`${TOOL_ACTION_STYLES[tc.action]} text-[9px] h-4 px-1.5 uppercase`}>{tc.action}</Badge>
                <span className="font-mono font-semibold text-gray-800">{tc.tool?.name}</span>
                <Badge className="bg-gray-100 text-gray-600 text-[9px] h-4 px-1.5">
                    {tc.scope === "general" ? "general" : `state: ${tc.scope}`}
                </Badge>
                {tc.tool?.type && tc.tool.type !== "custom" && (
                    <Badge className="bg-blue-100 text-blue-700 text-[9px] h-4 px-1.5">{tc.tool.type}</Badge>
                )}
            </div>
            {tc.tool?.description && <p className="text-gray-600 mt-1">{tc.tool.description}</p>}
            {tc.tool?.url && <p className="text-gray-400 font-mono mt-0.5 break-all">{tc.tool.url}</p>}
            {tc.tool?.parameters?.properties && (
                <p className="text-gray-400 mt-0.5">params: {Object.keys(tc.tool.parameters.properties).join(", ")}</p>
            )}
        </div>
    );
}

export function NoteInput({ value, onChange, disabled }) {
    return (
        <textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            rows={1}
            placeholder="Optional note for Claude about this change — e.g. 'keep the original greeting' or 'make this shorter'..."
            className="w-full mt-1.5 px-2.5 py-1.5 text-[11px] border border-dashed border-orange-200 rounded-md bg-orange-50/40 placeholder:text-orange-300 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:bg-white resize-y"
        />
    );
}

// Modal shown when Claude proposes prompt changes (from chat, a test-call fix,
// or a real campaign-call fix). proposal.changes: [{ target, currentPrompt, proposedPrompt }]
// onApply(changes, toolChanges): caller decides what "apply" means (apply to
// the open editor's local state, or push straight to Retell via updateAiAgent).
export function ProposalModal({ proposal, agentId, onApply, onClose, applyLabel = "Apply to editor", applying = false }) {
    // Local copy so "revise with notes" can replace the content in place
    const [view, setView] = useState(proposal);
    const [notes, setNotes] = useState({}); // { targetKey: note }
    const [generalNote, setGeneralNote] = useState("");
    const [revising, setRevising] = useState(false);

    useEffect(() => {
        setView(proposal);
        setNotes({});
        setGeneralNote("");
    }, [proposal]);

    if (!proposal || !view) return null;

    const changes = view.changes || [];
    const toolChanges = view.toolChanges || [];
    const hasNotes = generalNote.trim() || Object.values(notes).some((n) => n && n.trim());
    const busy = revising || applying;

    const revise = async () => {
        setRevising(true);
        try {
            const noteList = Object.entries(notes)
                .filter(([, n]) => n && n.trim())
                .map(([target, note]) => ({ target, note: note.trim() }));
            const res = await reviseAgentProposal(agentId, {
                changes: changes.map(({ target, proposedPrompt }) => ({ target, proposedPrompt })),
                toolChanges,
                notes: noteList,
                generalNote: generalNote.trim()
            });
            if (res?.status === 200 && res.data) {
                setView({ ...view, ...res.data });
                setNotes({});
                setGeneralNote("");
                toast.success("Claude revised the proposal with your notes");
            } else {
                toast.error(res?.data?.message || "Revision failed");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Revision failed");
        } finally {
            setRevising(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
            <div
                className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-orange-500" />
                        <h3 className="text-sm font-semibold text-gray-900">
                            Proposed changes
                            {changes.length > 0 && ` — ${changes.length} prompt${changes.length === 1 ? "" : "s"}`}
                            {toolChanges.length > 0 && ` — ${toolChanges.length} tool${toolChanges.length === 1 ? "" : "s"}`}
                        </h3>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="p-5 overflow-y-auto flex-1 space-y-4">
                    {view.explanation && (
                        <p className="text-xs text-gray-600 bg-orange-50 border border-orange-100 rounded-lg p-2.5">
                            {view.explanation}
                        </p>
                    )}
                    {changes.map((c) => (
                        <div key={c.target}>
                            <Badge className={`mb-1.5 text-[10px] h-5 ${c.target === "general" ? "bg-violet-100 text-violet-700" : "bg-blue-100 text-blue-700"}`}>
                                {c.target === "general" ? "General prompt" : `State: ${c.target}`}
                            </Badge>
                            <DiffView oldText={c.currentPrompt} newText={c.proposedPrompt} />
                            <NoteInput
                                value={notes[c.target]}
                                onChange={(v) => setNotes((n) => ({ ...n, [c.target]: v }))}
                                disabled={busy}
                            />
                        </div>
                    ))}
                    {toolChanges.length > 0 && (
                        <div>
                            <Badge className="mb-1.5 text-[10px] h-5 bg-orange-100 text-orange-700">Tool changes</Badge>
                            <div className="space-y-2">
                                {toolChanges.map((tc, i) => (
                                    <div key={i}>
                                        <ToolChangeCard tc={tc} />
                                        <NoteInput
                                            value={notes[`tool:${tc.tool?.name}`]}
                                            onChange={(v) => setNotes((n) => ({ ...n, [`tool:${tc.tool?.name}`]: v }))}
                                            disabled={busy}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div>
                        <Badge className="mb-1.5 text-[10px] h-5 bg-gray-100 text-gray-600">Overall feedback</Badge>
                        <NoteInput value={generalNote} onChange={setGeneralNote} disabled={busy} />
                    </div>
                </div>
                <div className="px-5 py-3.5 border-t border-gray-100 flex items-center gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        className="border-orange-300 text-orange-700 hover:bg-orange-50"
                        onClick={revise}
                        disabled={!hasNotes || busy}
                        title={hasNotes ? "Send your notes to Claude and get a revised proposal" : "Write a note first"}
                    >
                        {revising ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                        {revising ? "Claude is revising..." : "Revise with my notes"}
                    </Button>
                    <div className="ml-auto flex gap-2">
                        <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Discard</Button>
                        <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => onApply(changes, toolChanges)}
                            disabled={busy}
                        >
                            {applying ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
                            {applying ? "Applying..." : applyLabel}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
